import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import {
  getProductDefaultInfo,
  getCartProductAvailabilityInfo,
  getCartProductInfo,
} from '../../../../utilities/selectors';
import {
  getZlataPriadkaProductDefaultInfo,
  getZlataPriadkaCartProductInfo,
  getZlataPriadkaTrueCount,
} from '../../selectors';
import { OrderNrInput } from '../../Product/OrderNrInput';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import { TableTd, TableRow } from '../../General/Table';
import {
  FlexCol,
  FlexRowCenterHorizontal,
  FlexRow,
  FlexRowCenterVertical,
} from '../../../Defaults/Flexbox/Flexbox';
import { TextP } from '../../../Defaults/Text';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { CountSetter } from '../../ProductDetail/CountSetter';
import { FormInput } from '../../Form/FormInput';
import { DotDelimeter } from '../../General/DotDelimeter';
import { __ } from 'react-i18n';
import { MobileTitleValue } from './MobileTitleValue';
import {
  getSymbolFromCurrency,
  arrayToObject,
  prop,
  round,
} from '../../../../utilities';
import {
  STOCK_INTERNAL,
  STOCK_EXTERNAL,
  NO_STOCK,
  ON_STOCK,
} from '../../constants';

export interface Props {
  product: any;
  addToCart: any;
  removeFromCart: any;
  theme: any;
  hasMinAmountInfo?: boolean;
  isPriceWithVat: boolean;
  handlePaintChange: (goodId: number, wantPaint: boolean) => void;
}

function ItemsTabRowMobile({
  product,
  addToCart,
  theme,
  hasMinAmountInfo,
  removeFromCart,
  isPriceWithVat,
  handlePaintChange,
}: Props) {
  const {
    currency,
    picture,
    url,
    name,
    ordernr: currentOrdernr,
    productId,
    goodId,
    sumPrice,
    productNote,
    plu,
  } = getCartProductInfo(product);
  const {
    unit,
    price,
    priceWithoutVat,
    productPackages,
  } = getZlataPriadkaProductDefaultInfo(product, 'good');
  const step = unit === 'm' ? 0.1 : 1;
  const { count: initialCount } = getZlataPriadkaCartProductInfo(product);
  const [count, setCount] = React.useState(initialCount.toString());
  const onStock = 1;
  const [press, setPress] = React.useState(productNote ? true : false);

  const correctFinalPrice = isPriceWithVat ? sumPrice : sumPrice;
  const pricePerUnit = round(correctFinalPrice / count, 2);

  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < step) {
        newCount = step.toFixed(3);
      }
      newCountNumber = parseFloat(newCount);
      const countDiff = -(initialCount - newCountNumber);
      const correctCount = getZlataPriadkaTrueCount(unit === 'Mks', countDiff);
      addToCart(product, correctCount);
    }

    setCount(newCount);
  };

  const handleRemoveClick = () => {
    removeFromCart(productId, goodId);
  };

  const onStockCorrect = onStock;
  const storesMap: any = null;

  const stockText = onStockCorrect > 0 ? ON_STOCK : NO_STOCK;
  const color = onStockCorrect > 0 ? '#2CC5B7' : '#FF0000';

  // const handlePressChange = () => {
  //   handlePaintChange(goodId, !press);
  //   setPress(!press);
  // };

  return (
    <Wrapper noBorderBottom={hasMinAmountInfo}>
      <ImageWrapper to={url}>
        <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
      </ImageWrapper>
      <UpperWrapper>
        <ImageNameWrapper>
          <NameInputWrapper>
            <NameLink to={url}>
              <Name>{name}</Name>
            </NameLink>
            <OrderNrStockWrapper>
              <Stock color={'#A5BBA2'}>{stockText}</Stock>
            </OrderNrStockWrapper>
          </NameInputWrapper>
        </ImageNameWrapper>
        <BottomWrapper>
          <CountSetter
            onChange={handleCountChange}
            unit={unit}
            count={count}
            step={step}
          />
          <Price>{`${correctFinalPrice} ${getSymbolFromCurrency(
            currency,
          )}`}</Price>
        </BottomWrapper>
      </UpperWrapper>
      <SvgIcon
        onClick={handleRemoveClick}
        width={16}
        height={16}
        marginLeft={-8}
        icon={IconType.x}
        viewBox="0 0 24 24"
        alt="Odstrániť produkt"
        title="Odstrániť produkt"
        fill={'black'}
        cursor={'pointer'}
      />
    </Wrapper>
  );
}

const Wrapper = styled(FlexRow)<{ noBorderBottom: boolean }>`
  width: 100%;
  margin: 0 auto;
  border-bottom: ${({ noBorderBottom, theme }) =>
    !noBorderBottom ? theme.borders.primary : 'none'};
  padding: ${rem(24)} 0;
  justify-content: space-between;

  ${({ theme }) => theme.mediab.s450`
      margin: 0 -${rem(16)};
      padding:  ${rem(16)} ${rem(16)};
      max-width: initial;
      width: calc(100vw);
  `}

  svg {
    position: relative;
    top: ${rem(5)};
  }
`;

const UpperWrapper = styled(FlexCol)`
  margin-bottom: ${rem(24)};
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  ${({ theme }) => theme.mediab.m760`
    margin-bottom: 0;
  `}
`;

const ImageNameWrapper = styled(FlexRowCenterVertical)``;

const BottomWrapper = styled(FlexRowCenterVertical)`
  margin: ${rem(16)} 0;
  justify-content: flex-end;
  width: 100%;
  justify-content: space-between;

  ${({ theme }) => theme.mediab.s450`
      max-width: initial;
      margin: 0;
  `}
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
  width: ${rem(48)};
  height: ${rem(48)};

  ${({ theme }) => theme.mediab.l925`
    width: ${rem(80)};
    height: ${rem(80)};
  `}
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;

  ${({ theme }) => theme.mediab.l925`
    width: ${rem(80)};
    height: ${rem(80)};
  `}
`;

const NameInputWrapper = styled(FlexCol)`
  max-width: initial;
`;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  margin-bottom: ${rem(8)};

  color: ${({ theme }) => theme.colors.black};
  text-align: left;
  font-family: ${({ theme }) => theme.font.primary};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(150)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }
`;

const OrderNrStockWrapper = styled(FlexRowCenterVertical)`
  height: ${rem(18)};
`;

const OrderNr = styled(Name)`
  color: #aaaaaa;
  font-size: ${rem(13)};
  margin: 0;

  &:hover {
    color: inherit;
  }
`;

const Stock = styled(OrderNr)<{ color: string }>`
  color: ${({ color }) => color};
  font-size: ${rem(14)};
  margin: 0;

  &:hover {
    color: ${({ color }) => color};
  }
`;

const Price = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  color: black;
  font-family: ${({ theme }) => theme.font.primary};
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const WithTheme: any = withTheme(ItemsTabRowMobile);
export { WithTheme as ItemsTabRowMobile, StyledTableRow };
